import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PlacementAnalyticsService from './PlacementAnalyticsService';
import { directoryByCompanyPath, isDirectoryLinkEnabled, type PlacementRoleBase } from './deepLinks';

interface Props {
    instituteId: number | string;
    roleBase: PlacementRoleBase;
}

const PlacementReconsiderationList = ({ instituteId, roleBase }: Props) => {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'A' | 'B'>('A');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        PlacementAnalyticsService.getReconsideration(instituteId)
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load reconsideration');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [instituteId]);

    if (loading) return <div className="text-muted font-13">Loading reconsideration…</div>;
    if (error) return <div className="alert alert-danger py-2 font-13">{error}</div>;
    if (!data?.uploadId) {
        return <div className="alert alert-info py-2 font-13">No ACTIONABLES upload with reconsideration data.</div>;
    }

    const hasA = (data.listA?.length || 0) > 0;
    const hasB = (data.listB?.length || 0) > 0;
    const tabToShow = hasA ? activeTab : 'B';

    return (
        <div>
            <h3 className="placement-section-title">Placement Reconsideration List</h3>
            {(hasA || hasB) && (
                <div className="placement-subtabs">
                    {hasA && (
                        <button
                            type="button"
                            className={`placement-subtab ${tabToShow === 'A' ? 'active' : ''}`}
                            onClick={() => setActiveTab('A')}>
                            List A — Poor on All Counts
                        </button>
                    )}
                    {hasB && (
                        <button
                            type="button"
                            className={`placement-subtab ${tabToShow === 'B' ? 'active' : ''}`}
                            onClick={() => setActiveTab('B')}>
                            List B — Poor Retention, Still Recruiting
                        </button>
                    )}
                </div>
            )}
            {tabToShow === 'A' && hasA && (
                <ListTable
                    title="List A — Below Median Salary, Low Offers, Poor Retention"
                    rows={data.listA}
                    roleBase={roleBase}
                />
            )}
            {tabToShow === 'B' && hasB && (
                <ListTable
                    title="List B — Poor Retention, Still Recruiting"
                    rows={data.listB}
                    roleBase={roleBase}
                />
            )}
            {!hasA && !hasB && (
                <div className="text-muted font-13">Reconsideration lists are empty.</div>
            )}
        </div>
    );
};

function dash(v: any) {
    return v == null || v === '' ? '—' : v;
}

function ListTable({
    title,
    rows,
    roleBase,
}: {
    title: string;
    rows: any[];
    roleBase: PlacementRoleBase;
}) {
    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="font-18 font-600 mb-0">{title}</h3>
                <span className="badge bg-secondary">{rows.length} companies</span>
            </div>
            <div className="table-responsive placement-card-table">
                <table className="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th>Total Offers</th>
                            <th>vs Median (₹L)</th>
                            <th>Current Alumni</th>
                            <th>Last 3 Years?</th>
                            <th>Retention Category</th>
                            <th>Mean Salary</th>
                            <th>Retention Ratio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={`${r.listType}-${r.companyId}`}>
                                <td>
                                    {isDirectoryLinkEnabled(roleBase) ? (
                                        <Link to={directoryByCompanyPath(r.companyId)}>{r.companyName}</Link>
                                    ) : (
                                        r.companyName
                                    )}
                                </td>
                                <td>{dash(r.totalOffers)}</td>
                                <td>{dash(r.gapVsMedianCtc)}</td>
                                <td>{dash(r.currentAlumniCount)}</td>
                                <td>
                                    {r.cameInLast3Years == null ? '—' : r.cameInLast3Years ? 'Yes' : 'No'}
                                </td>
                                <td>{dash(r.retentionCategory)}</td>
                                <td>{dash(r.meanSalary)}</td>
                                <td>{dash(r.retentionRatio)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default PlacementReconsiderationList;
